import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';

interface RatingGroup {
  id: string;
  code: string;
  name: string;
}
interface Rating {
  id: string;
  code: string;
  description: string;
  definition: string;
  score_percent: number;
}

export default function RatingGroups() {
  const [groups, setGroups] = useState<RatingGroup[]>([]);
  const [selected, setSelected] = useState<RatingGroup | null>(null);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupForm, setGroupForm] = useState({ code: '', name: '' });
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [editingRatingId, setEditingRatingId] = useState<string | null>(null);
  const [ratingForm, setRatingForm] = useState({ code: '', description: '', definition: '', scorePercent: '' });
  const [deleteGroup, setDeleteGroup] = useState<RatingGroup | null>(null);
  const [error, setError] = useState('');

  const loadGroups = async () => {
    const res = await api.get('/pas/masters/rating-groups');
    setGroups(res.data.ratingGroups || []);
  };

  const loadRatings = async (group: RatingGroup) => {
    setSelected(group);
    const res = await api.get(`/pas/masters/rating-groups/${group.id}/ratings`);
    setRatings(res.data.ratings || []);
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const saveGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/pas/masters/rating-groups', groupForm);
      setShowGroupForm(false);
      setGroupForm({ code: '', name: '' });
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const deleteGroupConfirmed = async () => {
    if (!deleteGroup) return;
    await api.delete(`/pas/masters/rating-groups/${deleteGroup.id}`);
    setDeleteGroup(null);
    if (selected?.id === deleteGroup.id) setSelected(null);
    loadGroups();
  };

  const openRatingForm = (rating?: Rating) => {
    if (rating) {
      setEditingRatingId(rating.id);
      setRatingForm({
        code: rating.code,
        description: rating.description,
        definition: rating.definition || '',
        scorePercent: String(rating.score_percent),
      });
    } else {
      setEditingRatingId(null);
      setRatingForm({ code: '', description: '', definition: '', scorePercent: '' });
    }
    setError('');
    setShowRatingForm(true);
  };

  const saveRating = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setError('');
    try {
      if (editingRatingId) {
        await api.put(`/pas/masters/ratings/${editingRatingId}`, ratingForm);
      } else {
        await api.post(`/pas/masters/rating-groups/${selected.id}/ratings`, ratingForm);
      }
      setShowRatingForm(false);
      loadRatings(selected);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save rating');
    }
  };

  const deleteRating = async (rating: Rating) => {
    if (!selected) return;
    await api.delete(`/pas/masters/ratings/${rating.id}`);
    loadRatings(selected);
  };

  return (
    <div className="p-6 grid grid-cols-2 gap-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold text-gray-800">Rating Groups</h1>
          <Button
            onClick={() => {
              setGroupForm({ code: '', name: '' });
              setError('');
              setShowGroupForm(true);
            }}
          >
            + New
          </Button>
        </div>
        <Table
          columns={[
            { key: 'code', header: 'Code' },
            { key: 'name', header: 'Name' },
            {
              key: 'actions',
              header: 'Actions',
              render: (row) => (
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => loadRatings(row)}>
                    Ratings
                  </Button>
                  <Button variant="ghost" className="text-red-600" onClick={() => setDeleteGroup(row)}>
                    Delete
                  </Button>
                </div>
              ),
            },
          ]}
          rows={groups}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">{selected ? `Ratings — ${selected.name}` : 'Select a group'}</h2>
          {selected && <Button onClick={() => openRatingForm()}>+ Rating</Button>}
        </div>
        {selected && (
          <Table
            columns={[
              { key: 'code', header: 'Code' },
              { key: 'description', header: 'Description' },
              { key: 'score_percent', header: 'Score %' },
              {
                key: 'actions',
                header: 'Actions',
                render: (row) => (
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => openRatingForm(row)}>
                      Edit
                    </Button>
                    <Button variant="ghost" className="text-red-600" onClick={() => deleteRating(row)}>
                      Delete
                    </Button>
                  </div>
                ),
              },
            ]}
            rows={ratings}
          />
        )}
      </div>

      <Modal isOpen={showGroupForm} onClose={() => setShowGroupForm(false)} title="New Rating Group">
        <form onSubmit={saveGroup} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={groupForm.code} onChange={(e) => setGroupForm({ ...groupForm, code: e.target.value })} />
          <Input label="Name" maxLength={50} required value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowGroupForm(false)}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showRatingForm} onClose={() => setShowRatingForm(false)} title={editingRatingId ? 'Edit Rating' : 'New Rating'}>
        <form onSubmit={saveRating} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={ratingForm.code} onChange={(e) => setRatingForm({ ...ratingForm, code: e.target.value })} />
          <Input label="Description" maxLength={50} required value={ratingForm.description} onChange={(e) => setRatingForm({ ...ratingForm, description: e.target.value })} />
          <Input label="Definition" value={ratingForm.definition} onChange={(e) => setRatingForm({ ...ratingForm, definition: e.target.value })} />
          <Input label="Score %" type="number" required value={ratingForm.scorePercent} onChange={(e) => setRatingForm({ ...ratingForm, scorePercent: e.target.value })} />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowRatingForm(false)}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteGroup}
        message={`Delete rating group "${deleteGroup?.name}"?`}
        onConfirm={deleteGroupConfirmed}
        onCancel={() => setDeleteGroup(null)}
      />
    </div>
  );
}
